/**
 * lib/clickGuidance.js — Phase E v8c.13 (2026-02-28).
 *
 * Generates the prominent **click-order + landmark guidance** string
 * rendered directly below the preset dropdown in the AddZonePanel.
 *
 * The string is the single most important UX hint the operator sees
 * during multi-zone capture — it answers:
 *   • HOW MANY points to click (2 / 4 / 1-double-click)
 *   • IN WHICH ORDER (left→right / clockwise / spec'd corner sequence)
 *   • WHERE EXACTLY (named field landmarks — Schnittpunkt Goalline /
 *     Brick-Kreuz / Endzone-Ecke)
 *
 * Why is this a separate module?
 * ──────────────────────────────
 * 1. The guidance text is operator-facing copy that may need to be
 *    A/B tested or localised — keeping it OUT of `fieldPresets.js`
 *    means we can iterate without touching the geometric registry.
 * 2. Pure-function unit tests (`clickGuidance.test.js`) lock the
 *    contract so a future "subtle wording change" doesn't silently
 *    regress an important hint (e.g. losing "REIHENFOLGE: LINKS
 *    NACH RECHTS" on the goal-to-goal sidelines would re-introduce
 *    the 352 px DLT inversion bug).
 *
 * Returns an object so the UI can render the parts independently
 * (count badge + headline + sub-text):
 *
 *   {
 *     pointCount: 2 | 4 | 1,
 *     direction: "LINKS NACH RECHTS" | "IM UHRZEIGERSINN" | "DOPPELKLICK",
 *     headline: "👉 (2 Punkte • REIHENFOLGE: LINKS NACH RECHTS):",
 *     body: "Klicke ZUERST den Schnittpunkt oben LINKS …",
 *   }
 */

const POINT_COUNTS = { area: 4, line: 2, point: 1 };

// ── Diagram descriptors (v8c.13b · 2026-02-28) ────────────────────────
//
// Each preset can optionally carry a tiny SVG-blueprint descriptor that
// the `<ClickGuidanceDiagram>` React component turns into an inline
// 80×40 px micro-diagram next to the textual guidance. The schema
// lives in this data-only module so the JSX renderer can stay dumb +
// purely presentational.
//
// Coordinate system: 100×50 viewBox where (0,0) is top-left, the
// field outline fills the whole box. Numbered points are rendered as
// circles at the listed (x,y) with the listed label, connected in
// order by directional arrows.
//
// Schema:
//   diagram: {
//     kind:      "horizontal-line" | "vertical-line" | "polygon" | "point",
//     // Optional reference geometry the renderer draws underneath the
//     // numbered points (faint stroke) to give the operator spatial
//     // context. Values map to fixed SVG snippets in the component.
//     fieldHint: "full" | "endzone-left" | "endzone-right" | "brick-band"
//                | "midline" | "left-half" | "right-half" | null,
//     points:    [{ x, y, label }, …],   // 1-4 entries
//   }
const HORIZ_TOP = 12, HORIZ_BOT = 38;
const FAR_LEFT = 6, MID_LEFT = 22, CENTER = 50, MID_RIGHT = 78, FAR_RIGHT = 94;
// Helper for clockwise polygons starting top-left.
const polyClockwise = (x1, y1, x2, y2) => [
  { x: x1, y: y1, label: "1" },
  { x: x2, y: y1, label: "2" },
  { x: x2, y: y2, label: "3" },
  { x: x1, y: y2, label: "4" },
];

const DIAGRAMS = {
  // ── Longitudinal lines · LINKS → RECHTS ─────────────────────────────
  line_sideline_top_goal_to_goal: {
    kind: "horizontal-line", fieldHint: "full",
    points: [
      { x: MID_LEFT, y: HORIZ_TOP, label: "1" },
      { x: MID_RIGHT, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_sideline_bottom_goal_to_goal: {
    kind: "horizontal-line", fieldHint: "full",
    points: [
      { x: MID_LEFT, y: HORIZ_BOT, label: "1" },
      { x: MID_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_sideline_top_full: {
    kind: "horizontal-line", fieldHint: "full",
    points: [
      { x: FAR_LEFT, y: HORIZ_TOP, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_sideline_bottom_full: {
    kind: "horizontal-line", fieldHint: "full",
    points: [
      { x: FAR_LEFT, y: HORIZ_BOT, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_sideline_top_left_half: {
    kind: "horizontal-line", fieldHint: "midline",
    points: [
      { x: FAR_LEFT, y: HORIZ_TOP, label: "1" },
      { x: CENTER, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_sideline_top_right_half: {
    kind: "horizontal-line", fieldHint: "midline",
    points: [
      { x: CENTER, y: HORIZ_TOP, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_sideline_bottom_left_half: {
    kind: "horizontal-line", fieldHint: "midline",
    points: [
      { x: FAR_LEFT, y: HORIZ_BOT, label: "1" },
      { x: CENTER, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_sideline_bottom_right_half: {
    kind: "horizontal-line", fieldHint: "midline",
    points: [
      { x: CENTER, y: HORIZ_BOT, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_brick_to_brick_top: {
    kind: "horizontal-line", fieldHint: "brick-band",
    points: [
      { x: MID_LEFT + 8, y: HORIZ_TOP, label: "1" },
      { x: MID_RIGHT - 8, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_brick_to_brick_bottom: {
    kind: "horizontal-line", fieldHint: "brick-band",
    points: [
      { x: MID_LEFT + 8, y: HORIZ_BOT, label: "1" },
      { x: MID_RIGHT - 8, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_endzone_depth_top_left: {
    kind: "horizontal-line", fieldHint: "endzone-left",
    points: [
      { x: FAR_LEFT, y: HORIZ_TOP, label: "1" },
      { x: MID_LEFT, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_endzone_depth_top_right: {
    kind: "horizontal-line", fieldHint: "endzone-right",
    points: [
      { x: MID_RIGHT, y: HORIZ_TOP, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_endzone_depth_bottom_left: {
    kind: "horizontal-line", fieldHint: "endzone-left",
    points: [
      { x: FAR_LEFT, y: HORIZ_BOT, label: "1" },
      { x: MID_LEFT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_endzone_depth_bottom_right: {
    kind: "horizontal-line", fieldHint: "endzone-right",
    points: [
      { x: MID_RIGHT, y: HORIZ_BOT, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_goalline_to_brick_top_left: {
    kind: "horizontal-line", fieldHint: "endzone-left",
    points: [
      { x: MID_LEFT, y: HORIZ_TOP, label: "1" },
      { x: MID_LEFT + 8, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_goalline_to_brick_bottom_left: {
    kind: "horizontal-line", fieldHint: "endzone-left",
    points: [
      { x: MID_LEFT, y: HORIZ_BOT, label: "1" },
      { x: MID_LEFT + 8, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_goalline_to_brick_top_right: {
    kind: "horizontal-line", fieldHint: "endzone-right",
    points: [
      { x: MID_RIGHT - 8, y: HORIZ_TOP, label: "1" },
      { x: MID_RIGHT, y: HORIZ_TOP, label: "2" },
    ],
  },
  line_goalline_to_brick_bottom_right: {
    kind: "horizontal-line", fieldHint: "endzone-right",
    points: [
      { x: MID_RIGHT - 8, y: HORIZ_BOT, label: "1" },
      { x: MID_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  // ── Transverse lines · OBEN → UNTEN ─────────────────────────────────
  line_goalline_left: {
    kind: "vertical-line", fieldHint: "full",
    points: [
      { x: MID_LEFT, y: HORIZ_TOP, label: "1" },
      { x: MID_LEFT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_goalline_right: {
    kind: "vertical-line", fieldHint: "full",
    points: [
      { x: MID_RIGHT, y: HORIZ_TOP, label: "1" },
      { x: MID_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_backline_left: {
    kind: "vertical-line", fieldHint: "full",
    points: [
      { x: FAR_LEFT, y: HORIZ_TOP, label: "1" },
      { x: FAR_LEFT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_backline_right: {
    kind: "vertical-line", fieldHint: "full",
    points: [
      { x: FAR_RIGHT, y: HORIZ_TOP, label: "1" },
      { x: FAR_RIGHT, y: HORIZ_BOT, label: "2" },
    ],
  },
  line_midfield: {
    kind: "vertical-line", fieldHint: "midline",
    points: [
      { x: CENTER, y: HORIZ_TOP, label: "1" },
      { x: CENTER, y: HORIZ_BOT, label: "2" },
    ],
  },
  // ── Polygons · IM UHRZEIGERSINN ─────────────────────────────────────
  endzone: {
    kind: "polygon", fieldHint: "endzone-left",
    points: polyClockwise(FAR_LEFT, HORIZ_TOP, MID_LEFT, HORIZ_BOT),
  },
  brick_to_brick_rect: {
    kind: "polygon", fieldHint: "brick-band",
    points: polyClockwise(MID_LEFT + 8, HORIZ_TOP, MID_RIGHT - 8, HORIZ_BOT),
  },
  hauptspielfeld: {
    kind: "polygon", fieldHint: "full",
    points: polyClockwise(FAR_LEFT, HORIZ_TOP, FAR_RIGHT, HORIZ_BOT),
  },
  central_play: {
    kind: "polygon", fieldHint: "midline",
    points: polyClockwise(MID_LEFT, HORIZ_TOP, MID_RIGHT, HORIZ_BOT),
  },
  endzone_plus_brick: {
    kind: "polygon", fieldHint: "endzone-left",
    points: polyClockwise(FAR_LEFT, HORIZ_TOP, MID_LEFT + 8, HORIZ_BOT),
  },
  half_central_play: {
    kind: "polygon", fieldHint: "midline",
    points: polyClockwise(CENTER, HORIZ_TOP, MID_RIGHT, HORIZ_BOT),
  },
  brick_zone: {
    kind: "polygon", fieldHint: "endzone-left",
    points: polyClockwise(MID_LEFT, HORIZ_TOP, MID_LEFT + 8, HORIZ_BOT),
  },
  goalline_to_midfield_left: {
    kind: "polygon", fieldHint: "left-half",
    points: polyClockwise(MID_LEFT, HORIZ_TOP, CENTER, HORIZ_BOT),
  },
  goalline_to_midfield_right: {
    kind: "polygon", fieldHint: "right-half",
    points: polyClockwise(CENTER, HORIZ_TOP, MID_RIGHT, HORIZ_BOT),
  },
  // ── Points · DOPPELKLICK ────────────────────────────────────────────
  point_brick_left: {
    kind: "point", fieldHint: "endzone-left",
    points: [{ x: MID_LEFT + 8, y: 25, label: "×" }],
  },
  point_brick_right: {
    kind: "point", fieldHint: "endzone-right",
    points: [{ x: MID_RIGHT - 8, y: 25, label: "×" }],
  },
};

/** Coordinates of any diagram primitive carried by a preset id.
 *  Returns ``null`` if the preset has no diagram entry — caller
 *  can either render fallback text or hide the diagram column. */
export function diagramFor(presetId, { side } = {}) {
  const base = DIAGRAMS[presetId];
  if (!base) return null;
  // For `endzone` the diagram defaults to the LEFT endzone; swap to
  // the right when the operator picked the right side.
  if (presetId === "endzone" && side === "right") {
    return {
      ...base,
      fieldHint: "endzone-right",
      points: polyClockwise(MID_RIGHT, HORIZ_TOP, FAR_RIGHT, HORIZ_BOT),
    };
  }
  return base;
}

// ── Preset-specific overrides ────────────────────────────────────────
// Maps preset.id → an override object. Anything not in this map falls
// through to the kind-based generic guidance below.
const OVERRIDES = {
  // ── LINIEN (2 Klicks · LINKS → RECHTS) ──────────────────────────────
  line_sideline_top_goal_to_goal: {
    body: "Klicke ZUERST den Schnittpunkt oben LINKS "
        + "(Goalline / Seitenlinie) und DANACH oben RECHTS. "
        + "Klicke KEINE Teilstücke! Beide Punkte müssen im Bild sichtbar sein.",
  },
  line_sideline_bottom_goal_to_goal: {
    body: "Klicke ZUERST den Schnittpunkt unten LINKS "
        + "(Goalline / Seitenlinie) und DANACH unten RECHTS. "
        + "Klicke KEINE Teilstücke! Beide Punkte müssen im Bild sichtbar sein.",
  },
  line_sideline_top_full: {
    body: "Klicke ZUERST die obere LINKE Eckfahne (Backline-Ecke), "
        + "DANACH die obere RECHTE Eckfahne. Volle Längslinie inkl. Endzonen.",
  },
  line_sideline_bottom_full: {
    body: "Klicke ZUERST die untere LINKE Eckfahne (Backline-Ecke), "
        + "DANACH die untere RECHTE Eckfahne. Volle Längslinie inkl. Endzonen.",
  },
  line_sideline_top_left_half: {
    body: "Klicke ZUERST oben LINKS (Backline-Ecke), DANACH die Mittellinien-"
        + "Kreuzung oben (Schnittpunkt Mittellinie / obere Längslinie).",
  },
  line_sideline_top_right_half: {
    body: "Klicke ZUERST die Mittellinien-Kreuzung oben (Schnittpunkt Mittel-"
        + "linie / obere Längslinie), DANACH oben RECHTS (Backline-Ecke).",
  },
  line_sideline_bottom_left_half: {
    body: "Klicke ZUERST unten LINKS (Backline-Ecke), DANACH die Mittellinien-"
        + "Kreuzung unten (Schnittpunkt Mittellinie / untere Längslinie).",
  },
  line_sideline_bottom_right_half: {
    body: "Klicke ZUERST die Mittellinien-Kreuzung unten, DANACH unten "
        + "RECHTS (Backline-Ecke).",
  },
  // ── Brick-Brick Längs-Segmente ──────────────────────────────────────
  line_brick_to_brick_top: {
    body: "Klicke ZUERST das obere LINKE Brick-Kreuz (Projektion der linken "
        + "Brick-Marke auf die obere Längslinie) und DANACH das obere "
        + "RECHTE Brick-Kreuz.",
  },
  line_brick_to_brick_bottom: {
    body: "Klicke ZUERST das untere LINKE Brick-Kreuz (Projektion der linken "
        + "Brick-Marke auf die untere Längslinie) und DANACH das untere "
        + "RECHTE Brick-Kreuz.",
  },
  // ── Endzone-Tiefen ──────────────────────────────────────────────────
  line_endzone_depth_top_left: {
    body: "Klicke ZUERST die obere LINKE Backline-Ecke und DANACH die "
        + "obere LINKE Goalline-Ecke (Endzone-Tiefe entlang oberer Längslinie).",
  },
  line_endzone_depth_top_right: {
    body: "Klicke ZUERST die obere RECHTE Goalline-Ecke und DANACH die "
        + "obere RECHTE Backline-Ecke (Endzone-Tiefe entlang oberer Längslinie).",
  },
  line_endzone_depth_bottom_left: {
    body: "Klicke ZUERST die untere LINKE Backline-Ecke und DANACH die "
        + "untere LINKE Goalline-Ecke.",
  },
  line_endzone_depth_bottom_right: {
    body: "Klicke ZUERST die untere RECHTE Goalline-Ecke und DANACH die "
        + "untere RECHTE Backline-Ecke.",
  },
  // ── Goalline ↔ Brick Längs-Segmente ─────────────────────────────────
  line_goalline_to_brick_top_left: {
    body: "Klicke ZUERST die obere LINKE Goalline-Ecke und DANACH das "
        + "obere LINKE Brick-Kreuz.",
  },
  line_goalline_to_brick_bottom_left: {
    body: "Klicke ZUERST die untere LINKE Goalline-Ecke und DANACH das "
        + "untere LINKE Brick-Kreuz.",
  },
  line_goalline_to_brick_top_right: {
    body: "Klicke ZUERST das obere RECHTE Brick-Kreuz und DANACH die "
        + "obere RECHTE Goalline-Ecke.",
  },
  line_goalline_to_brick_bottom_right: {
    body: "Klicke ZUERST das untere RECHTE Brick-Kreuz und DANACH die "
        + "untere RECHTE Goalline-Ecke.",
  },
  // ── Querlinien (TOP → BOTTOM) ───────────────────────────────────────
  line_goalline_left: {
    direction: "OBEN NACH UNTEN",
    body: "Klicke ZUERST die OBEN-LINKE Goalline-Ecke und DANACH die "
        + "UNTEN-LINKE Goalline-Ecke (vertikale Querlinie).",
  },
  line_goalline_right: {
    direction: "OBEN NACH UNTEN",
    body: "Klicke ZUERST die OBEN-RECHTE Goalline-Ecke und DANACH die "
        + "UNTEN-RECHTE Goalline-Ecke (vertikale Querlinie).",
  },
  line_backline_left: {
    direction: "OBEN NACH UNTEN",
    body: "Klicke ZUERST die OBEN-LINKE Backline-Ecke und DANACH die "
        + "UNTEN-LINKE Backline-Ecke (vertikale Querlinie).",
  },
  line_backline_right: {
    direction: "OBEN NACH UNTEN",
    body: "Klicke ZUERST die OBEN-RECHTE Backline-Ecke und DANACH die "
        + "UNTEN-RECHTE Backline-Ecke (vertikale Querlinie).",
  },
  line_midfield: {
    direction: "OBEN NACH UNTEN",
    body: "Klicke ZUERST die OBERE Mittellinien-Kreuzung (Schnittpunkt mit "
        + "der oberen Längslinie) und DANACH die UNTERE Mittellinien-Kreuzung.",
  },
  // ── FLÄCHEN (4 Klicks · UHRZEIGERSINN) ──────────────────────────────
  endzone: {
    // 🆕 v8c.16d (2026-02-28) · Text harmonised with SVG diagram +
    // new WIDTH-mode contract: ①→② is the SHORT endzone-depth side
    // along a sideline (10 m for U17), ②→③ is the GOALLINE itself
    // (25 m for U17). Operator clicks CW starting from the OUTER
    // backline corner.
    body: "Starte oben links an der Hecke/Zaun und klicke alle 4 Ecken "
        + "der ausgewählten Endzone IM UHRZEIGERSINN ab:\n"
        + " ① Backline-Ecke OBEN (Eckfahne hinten, obere Seitenlinie · AUSSEN am Spielfeldrand) →\n"
        + " ② Goalline-Ecke OBEN (Hütchen vorne, Feld-Inneres · obere Seitenlinie) →\n"
        + " ③ Goalline-Ecke UNTEN (Hütchen vorne, Feld-Inneres · untere Seitenlinie) →\n"
        + " ④ Backline-Ecke UNTEN (Eckfahne hinten, untere Seitenlinie · AUSSEN am Spielfeldrand).",
  },
  brick_to_brick_rect: {
    body: "Klicke EXAKT in dieser Reihenfolge: (1) Eigene Brick-Marke LINKS "
        + "(2) Eigene Brick-Marke RECHTS (3) Gegnerische Brick-Marke RECHTS "
        + "(4) Gegnerische Brick-Marke LINKS. KEINE Eckfahnen!",
  },
  hauptspielfeld: {
    body: "Klicke alle 4 äußeren Eckfahnen des gesamten Spielfelds (inkl. "
        + "Endzonen) im Uhrzeigersinn: oben links → oben rechts → unten "
        + "rechts → unten links.",
  },
  central_play: {
    body: "Klicke die 4 Goalline-Ecken (Spielfläche ohne Endzonen) im "
        + "Uhrzeigersinn: Linke Goalline oben → Rechte Goalline oben → "
        + "Rechte Goalline unten → Linke Goalline unten.",
  },
  endzone_plus_brick: {
    // 🆕 v8c.16d · Same backline-first CW order, but the "front" edge
    // is the brick mark (8-20 m past goalline inside the field of play).
    body: "Klicke 4 Ecken IM UHRZEIGERSINN (Backline → Brick · gleiche "
        + "Logik wie Endzone, nur die Vorderkante ist die Brick-Linie):\n"
        + " ① Backline-Ecke OBEN (Eckfahne hinten, obere Seitenlinie) →\n"
        + " ② Brick-Kreuz OBEN (auf der oberen Seitenlinie) →\n"
        + " ③ Brick-Kreuz UNTEN (auf der unteren Seitenlinie) →\n"
        + " ④ Backline-Ecke UNTEN (Eckfahne hinten, untere Seitenlinie).",
  },
  half_central_play: {
    body: "Klicke die 4 Ecken der gewählten Spielfeld-Hälfte (Mittellinie → "
        + "Goalline) im Uhrzeigersinn — startet oben an der Mittellinie.",
  },
  brick_zone: {
    // 🆕 v8c.16d · brick_zone lives in CENTRAL PLAY between the
    // goalline and the brick mark — "back" edge (closer to endzone)
    // is the goalline; "front" edge (deeper in central play) is the
    // brick mark line. Same backline-first CW order convention.
    body: "Klicke 4 Ecken IM UHRZEIGERSINN (Goalline → Brick im Spielfeld-"
        + "Inneren):\n"
        + " ① Goalline-Ecke OBEN (Hütchen, obere Seitenlinie) →\n"
        + " ② Brick-Kreuz OBEN (auf der oberen Seitenlinie) →\n"
        + " ③ Brick-Kreuz UNTEN (auf der unteren Seitenlinie) →\n"
        + " ④ Goalline-Ecke UNTEN (Hütchen, untere Seitenlinie).",
  },
  // ── Multi-Zone halves (Phase E v8c.2) ───────────────────────────────
  goalline_to_midfield_left: {
    body: "Klicke die 4 Ecken der LINKEN Spielfeld-Hälfte (Linke Goalline "
        + "→ Mittellinie) im Uhrzeigersinn ab.",
  },
  goalline_to_midfield_right: {
    body: "Klicke die 4 Ecken der RECHTEN Spielfeld-Hälfte (Mittellinie → "
        + "Rechte Goalline) im Uhrzeigersinn ab.",
  },
  // ── PUNKTE (1 DoppelKlick) ──────────────────────────────────────────
  point_brick_left: {
    body: "DoppelKlick exakt auf das gemalte X / den Cone der LINKEN "
        + "Brick-Marke (20 m vor der linken Goalline).",
  },
  point_brick_right: {
    body: "DoppelKlick exakt auf das gemalte X / den Cone der RECHTEN "
        + "Brick-Marke (20 m vor der rechten Goalline).",
  },
};

/** Build the operator-facing click-guidance object for a preset.
 *  Returns the same shape regardless of preset kind so the UI can
 *  render a uniform layout (headline + body + count badge + diagram). */
export function clickGuidanceFor(preset, { side } = {}) {
  if (!preset) return null;
  const kind = preset.kind || "area";
  const pointCount = POINT_COUNTS[kind] ?? 4;
  const override = OVERRIDES[preset.id] || {};

  // Pick a default direction label based on kind + axis.
  let direction;
  if (override.direction) {
    direction = override.direction;
  } else if (kind === "point") {
    direction = "DOPPELKLICK";
  } else if (kind === "area") {
    direction = "IM UHRZEIGERSINN";
  } else if (preset.pt0PtAxis === "width") {
    // Width-axis lines are vertical = goalline / backline / midfield.
    direction = "OBEN NACH UNTEN";
  } else {
    direction = "LINKS NACH RECHTS";
  }

  // Compose the prominent headline (rendered as a sub-header below
  // the preset selector).
  const headline = kind === "point"
    ? `👉 (1 Punkt • ${direction}):`
    : `👉 (${pointCount} Punkte • REIHENFOLGE: ${direction}):`;

  // Body text — preset-specific override OR a generic fallback.
  const body = override.body || (() => {
    if (kind === "point") {
      return preset.hint
        || "DoppelKlick exakt auf die markierte Marke / das Kreuz im Bild.";
    }
    if (kind === "line") {
      return preset.hint
        || "Klicke ZUERST den linken/oberen Endpunkt der Linie und "
           + "DANACH den rechten/unteren Endpunkt. Beide Punkte müssen "
           + "im Bild sichtbar sein. KEINE Teilstücke klicken!";
    }
    // area
    return preset.hint
      || "Klicke alle 4 Ecken der Zone IM UHRZEIGERSINN — starte oben links.";
  })();

  return { pointCount, direction, headline, body, diagram: diagramFor(preset.id, { side }) };
}
